import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const BetaMixDraftsContext = createContext(null);

const isValidCategory = (category) => category === 'pastry' || category === 'bakery';

const cloneMixes = (mixes = []) => mixes.map((mix) => ({ ...mix }));

export const BetaMixDraftsProvider = ({ children }) => {
  const [managedMixesByProgram, setManagedMixesByProgram] = useState({});

  const ensureManagedMixes = useCallback((programSlug, category, baseMixes = []) => {
    if (!programSlug || !isValidCategory(category)) {
      return;
    }

    setManagedMixesByProgram((current) => {
      const programMixes = current[programSlug] || {};

      if (Array.isArray(programMixes[category])) {
        return current;
      }

      return {
        ...current,
        [programSlug]: {
          ...programMixes,
          [category]: cloneMixes(baseMixes)
        }
      };
    });
  }, []);

  const getManagedMixes = useCallback(
    (programSlug, category, fallbackMixes = []) => {
      if (!programSlug || !isValidCategory(category)) {
        return fallbackMixes;
      }

      const programMixes = managedMixesByProgram[programSlug];
      const categoryMixes = programMixes?.[category];

      return Array.isArray(categoryMixes) ? categoryMixes : fallbackMixes;
    },
    [managedMixesByProgram]
  );

  const upsertManagedMix = useCallback((programSlug, category, mix) => {
    if (!programSlug || !isValidCategory(category) || !mix?.id) {
      return;
    }

    setManagedMixesByProgram((current) => {
      const programMixes = current[programSlug] || {};
      const currentCategoryMixes = Array.isArray(programMixes[category])
        ? programMixes[category]
        : [];

      const existingIndex = currentCategoryMixes.findIndex((item) => item.id === mix.id);

      if (existingIndex === -1) {
        return {
          ...current,
          [programSlug]: {
            ...programMixes,
            [category]: [...currentCategoryMixes, { ...mix }]
          }
        };
      }

      return {
        ...current,
        [programSlug]: {
          ...programMixes,
          [category]: currentCategoryMixes.map((item, index) =>
            index === existingIndex ? { ...item, ...mix } : item
          )
        }
      };
    });
  }, []);

  const removeManagedMix = useCallback((programSlug, category, mixId) => {
    if (!programSlug || !isValidCategory(category) || !mixId) {
      return;
    }

    setManagedMixesByProgram((current) => {
      const programMixes = current[programSlug];

      if (!programMixes || !Array.isArray(programMixes[category])) {
        return current;
      }

      return {
        ...current,
        [programSlug]: {
          ...programMixes,
          [category]: programMixes[category].filter((mix) => mix.id !== mixId)
        }
      };
    });
  }, []);

  const resetManagedMixes = useCallback((programSlug) => {
    if (!programSlug) {
      return;
    }

    setManagedMixesByProgram((current) => {
      if (!current[programSlug]) {
        return current;
      }

      const next = { ...current };
      delete next[programSlug];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      managedMixesByProgram,
      ensureManagedMixes,
      getManagedMixes,
      upsertManagedMix,
      removeManagedMix,
      resetManagedMixes
    }),
    [
      ensureManagedMixes,
      getManagedMixes,
      managedMixesByProgram,
      removeManagedMix,
      resetManagedMixes,
      upsertManagedMix
    ]
  );

  return <BetaMixDraftsContext.Provider value={value}>{children}</BetaMixDraftsContext.Provider>;
};

export const useBetaMixDrafts = () => {
  const context = useContext(BetaMixDraftsContext);

  if (!context) {
    throw new Error('useBetaMixDrafts must be used within BetaMixDraftsProvider.');
  }

  return context;
};
